// talkingAvatarErrors — error code → what the author reads and can do next.
//
// Codes come from two places: a refused POST /api/talking-avatar (400/429,
// phase "submit") and a job that failed in the worker (socket / GET, phase
// "job"). A failed job never costs credits, and the author is told so: every
// job-failure message ends with "Başarısız işlerden kredi düşülmez.".
//
// Seedance rejects realistic faces; that one gets the exact product copy
// "Bu avatar Seedance ile kullanılamıyor, Omni seçin" plus a "Omni'ye geç" action
// that only switches the model — nothing is resubmitted automatically.
//
// Seedance also works only with an AI-created library avatar (it is sent as a
// BytePlus virtual portrait): the model list marks its card unusable for other
// avatars, and a start that slips through is refused with
// MODEL_NOT_SUPPORTED_FOR_AVATAR + `reason`. Both read the same copy
// (seedanceAvatarUnsupportedText) and the refusal offers "Omni'ye geç".
//
// Insufficient credit is NOT handled here: callers detect it with
// isCreditRefusal() and hand it to onInsufficientBalance (the shared popup).
import { tt } from "./talkingAvatarI18n";

// Default (Turkish) copy, keyed like locales/*.js → TalkingAvatar.Errors.
export const TALKING_AVATAR_ERROR_DEFAULTS = {
  TEXT_TOO_LONG:
    "Metin bu model için çok uzun (~{{est}} sn / en fazla {{max}} sn). Lütfen kısaltın.",
  TEXT_PART_TOO_LONG:
    "Metinde tek parçada söylenemeyecek kadar uzun bir bölüm var; uzun cümleleri bölün.",
  VOICE_NOT_ALLOWED:
    "Bu ses kullanılamıyor. Listedeki ElevenLabs, kurum veya Azure seslerinden birini seçin.",
  VOICE_PROVIDER_UNAVAILABLE: "Bu ses sağlayıcısı şu anda kullanılamıyor; başka bir ses seçin.",
  AVATAR_REJECTED_SEEDANCE: "Bu avatar Seedance ile kullanılamıyor, Omni seçin",
  AVATAR_REJECTED: "Bu avatar seçilen model tarafından reddedildi; farklı bir avatar deneyin.",
  CONTENT_FILTERED: "Metin veya avatar sağlayıcının içerik filtresine takıldı.",
  VOICE_DRIFT: "Ses dönüşümü videoyla uyumlu çıkmadı; tekrar deneyin veya başka ses seçin.",
  PROVIDER_FAILED:
    "Video sağlayıcısı işlemi tamamlayamadı; biraz sonra veya başka modelle deneyin.",
  PRICE_LIST_MISSING: "Bu model için fiyat tanımlı değil; başka model seçin.",
  TOO_MANY_ACTIVE: "Aynı anda en fazla 3 doğal konuşan video üretilebilir.",
  MODEL_UNAVAILABLE: "Bu model şu anda kullanılamıyor; başka model seçin.",
  AVATAR_NOT_ALLOWED:
    "Bu avatar kullanılamıyor; kütüphaneden ya da kendi yüklediğiniz bir avatarı seçin.",
  TEXT_EMPTY: "Anlatım metni boş; lütfen metin girin.",
  RATIO_UNSUPPORTED: "Bu en/boy oranı seçilen modelde desteklenmiyor.",
  INVALID_REQUEST: "Gönderilen bilgiler geçersiz.",
  Generic: "Video oluşturulamadı; lütfen tekrar deneyin.",
};

const CREDIT_NOTE_DEFAULT = "Başarısız işlerden kredi düşülmez.";

// Model the "Omni'ye geç" action asks for (the veo-3.1 id runs Gemini Omni);
// the panel falls back to whichever Omni model is actually available.
export const VEO_FALLBACK_MODEL = "veo-3.1";

const KNOWN_CODES = new Set([
  "TALKING_AVATAR_TEXT_TOO_LONG",
  "TALKING_AVATAR_VOICE_NOT_ALLOWED",
  "TALKING_AVATAR_VOICE_PROVIDER_UNAVAILABLE",
  "TALKING_AVATAR_AVATAR_REJECTED",
  "TALKING_AVATAR_CONTENT_FILTERED",
  "TALKING_AVATAR_VOICE_DRIFT",
  "TALKING_AVATAR_PROVIDER_FAILED",
  "TALKING_AVATAR_TOO_MANY_ACTIVE",
  "TALKING_AVATAR_MODEL_UNAVAILABLE",
  "TALKING_AVATAR_AVATAR_NOT_ALLOWED",
  "TALKING_AVATAR_TEXT_EMPTY",
  "TALKING_AVATAR_RATIO_UNSUPPORTED",
  "TALKING_AVATAR_MODEL_NOT_SUPPORTED_FOR_AVATAR",
  "PRICE_LIST_MISSING",
  // Studio-side: a 400 from body validation (message array, no code).
  "INVALID_REQUEST",
]);

export function normalizeTalkingAvatarErrorCode(code) {
  if (code == null) return null;
  const upper = String(code).trim().toUpperCase();
  if (!upper) return null;
  if (KNOWN_CODES.has(upper)) return upper;
  if (KNOWN_CODES.has(`TALKING_AVATAR_${upper}`)) return `TALKING_AVATAR_${upper}`;
  return upper;
}

export const isSeedanceModel = (model) => /^seedance/i.test(String(model || ""));

// Why Seedance cannot be used with this avatar — the disabled model card and
// the MODEL_NOT_SUPPORTED_FOR_AVATAR refusal read the same copy. Only a
// BytePlus rejection has its own; NOT_AI_GENERATED, ASSET_LIBRARY_NOT_CONFIGURED
// (or no reason) all read "AI-created avatars only".
export function seedanceAvatarUnsupportedText(reason) {
  return reason === "AVATAR_REJECTED"
    ? tt(
        "TalkingAvatar.Model.SeedanceAvatarRejected",
        "Bu karakter bu modelle kullanılamıyor. Omni seçin."
      )
    : tt(
        "TalkingAvatar.Model.SeedanceNotAiAvatar",
        "Seedance yalnızca AI ile üretilmiş avatarlarla kullanılabilir. Bu avatar için Omni seçin."
      );
}

const errText = (key, vars) =>
  tt(`TalkingAvatar.Errors.${key}`, TALKING_AVATAR_ERROR_DEFAULTS[key], vars);

// Over the limit although the total fits: one part (an over-long sentence or
// token) does not fit a single segment — comparing seconds would read as
// nonsense ("~41 sn / en fazla 148 sn").
const isPartTooLong = ({ estimatedSeconds: est, maxSeconds: max }) =>
  est != null && max != null && Number(max) > 0 && Number(est) <= Number(max);

function spec(code, ctx) {
  switch (code) {
    case "TALKING_AVATAR_TEXT_TOO_LONG":
      if (isPartTooLong(ctx)) {
        return { text: errText("TEXT_PART_TOO_LONG"), action: { type: "focusText" } };
      }
      return {
        text: errText("TEXT_TOO_LONG", {
          est: ctx.estimatedSeconds != null ? Math.round(ctx.estimatedSeconds) : "?",
          max: ctx.maxSeconds != null ? Math.round(ctx.maxSeconds) : "?",
        }),
        action: { type: "focusText" },
      };
    case "TALKING_AVATAR_VOICE_NOT_ALLOWED":
      return {
        text: errText("VOICE_NOT_ALLOWED"),
        action: {
          type: "clearVoice",
          label: tt("TalkingAvatar.Failed.PickVoice", "Başka ses seç"),
        },
      };
    // The voice's provider (ElevenLabs / Azure) is not configured or not priced
    // right now: the voice is dropped and the picker opens on the providers.
    case "TALKING_AVATAR_VOICE_PROVIDER_UNAVAILABLE":
      return {
        text: errText("VOICE_PROVIDER_UNAVAILABLE"),
        action: {
          type: "clearVoice",
          label: tt("TalkingAvatar.Failed.PickVoice", "Başka ses seç"),
        },
      };
    case "TALKING_AVATAR_AVATAR_REJECTED":
      return isSeedanceModel(ctx.model)
        ? {
            text: errText("AVATAR_REJECTED_SEEDANCE"),
            action: {
              type: "switchModel",
              model: VEO_FALLBACK_MODEL,
              label: tt("TalkingAvatar.Failed.SwitchToVeo", "Omni'ye geç"),
            },
          }
        : { text: errText("AVATAR_REJECTED"), action: null };
    case "TALKING_AVATAR_MODEL_NOT_SUPPORTED_FOR_AVATAR":
      return {
        text: seedanceAvatarUnsupportedText(ctx.reason),
        action: {
          type: "switchModel",
          model: VEO_FALLBACK_MODEL,
          label: tt("TalkingAvatar.Failed.SwitchToVeo", "Omni'ye geç"),
        },
      };
    case "TALKING_AVATAR_CONTENT_FILTERED":
      return { text: errText("CONTENT_FILTERED"), action: null };
    case "TALKING_AVATAR_VOICE_DRIFT":
      return { text: errText("VOICE_DRIFT"), action: null };
    case "TALKING_AVATAR_PROVIDER_FAILED":
      return { text: errText("PROVIDER_FAILED"), action: null };
    case "PRICE_LIST_MISSING":
      return {
        text: errText("PRICE_LIST_MISSING"),
        action: {
          type: "pickModel",
          label: tt("TalkingAvatar.Failed.PickModel", "Başka model seç"),
        },
      };
    case "TALKING_AVATAR_MODEL_UNAVAILABLE":
      return {
        text: errText("MODEL_UNAVAILABLE"),
        action: {
          type: "pickModel",
          label: tt("TalkingAvatar.Failed.PickModel", "Başka model seç"),
        },
      };
    case "TALKING_AVATAR_TOO_MANY_ACTIVE":
      return { text: errText("TOO_MANY_ACTIVE"), action: null };
    case "TALKING_AVATAR_AVATAR_NOT_ALLOWED":
      return { text: errText("AVATAR_NOT_ALLOWED"), action: null };
    case "TALKING_AVATAR_TEXT_EMPTY":
      return { text: errText("TEXT_EMPTY"), action: { type: "focusText" } };
    case "TALKING_AVATAR_RATIO_UNSUPPORTED":
      return { text: errText("RATIO_UNSUPPORTED"), action: { type: "pickAspect" } };
    case "INVALID_REQUEST":
      return { text: errText("INVALID_REQUEST"), action: null };
    default:
      return null;
  }
}

// ctx: { model, estimatedSeconds, maxSeconds, reason, phase: "submit" | "job" }
// → { code, text, note, message, action }
export function describeTalkingAvatarError(rawCode, ctx = {}) {
  const normalized = normalizeTalkingAvatarErrorCode(rawCode);
  const found = normalized ? spec(normalized, ctx) : null;
  const code = found ? normalized : "GENERIC";
  const { text, action } = found || { text: errText("Generic"), action: null };
  const note =
    ctx.phase === "job" ? tt("TalkingAvatar.Failed.CreditNote", CREDIT_NOTE_DEFAULT) : null;
  const message = note ? `${text}${/[.!?…]$/.test(text) ? "" : "."} ${note}` : text;
  return { code, text, note, message, action };
}
